import 'whatwg-fetch';
import Config from './static-config';
import Authentication from './authentication';

export async function getHeaders(path) {
  if (/access_token=/i.test(path)) {
    return {};
  }
  if (!Authentication.isAuthenticated()) {
    const success = await Authentication.renewSession();
    if (!success) {
      await Authentication.logout();
      return {};
    }
  }
  return {
    Authorization: `Bearer ${Authentication.getAccessToken()}`
  };
}

function getUrl(path) {
  if (/^(https?|blob|data):(?:\/\/)?/i.test(path)) {
    return path;
  }
  return `${Config.api.url.replace(/\/+$/, '')}/${path.replace(/^\/+/, '')}`;
}

// Strip query strings so tokens never end up in error messages or Bugsnag
function describeUrl(url) {
  return url.split('?')[0];
}

/**
 * Thrown when the request never got a response (offline, dropped connection,
 * request cancelled by navigation). Browsers report these as a bare TypeError
 * with a vague message ("Failed to fetch", "Load failed", ...).
 */
export class NetworkError extends Error {
  constructor(method, url, cause) {
    super(`${method} ${describeUrl(url)} failed: ${cause && cause.message}`);
    this.name = 'NetworkError';
    this.method = method;
    this.url = describeUrl(url);
    this.cause = cause;
  }
}

export function isNetworkError(error) {
  return !!error && error.name === 'NetworkError';
}

const wait = ms => new Promise(resolve => setTimeout(resolve, ms));

/**
 * @param {string} path
 * @param {RequestInit & { retries?: number }} options
 *   retries: how many times to retry after a NetworkError (default 0)
 */
export async function fetch(path, options = {}) {
  const { retries = 0, ...fetchOptions } = options;
  const url = getUrl(path);
  const method = (fetchOptions.method || 'GET').toUpperCase();

  for (let attempt = 0; ; attempt += 1) {
    const headers = await getHeaders(path);
    try {
      return await window.fetch(url, {
        credentials: 'include',
        ...fetchOptions,
        headers: {
          ...(fetchOptions.headers || {}),
          ...headers
        }
      });
    } catch (e) {
      // Aborts are intentional, let them through untouched
      if (e && e.name === 'AbortError') {
        throw e;
      }
      if (attempt >= retries) {
        throw new NetworkError(method, url, e);
      }
      await wait(500 * Math.pow(3, attempt));
    }
  }
}
